import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { LibraryTrans, ToasterTrans, WorkoutTrans } from "../../utils/translations";
import bgImg from "../../assets/images/bg-5-1.png";
import DropdownButton from "../utilPages/DropdownButton";
import PopupModal from "../utilPages/PopupModal";
import { getPractices } from "../../services/practiceService";
import { getUser } from "../../services/userService";
import VideoDisplay from "../utilPages/VideoDisplay";
import SaveForLaterButton from "../utilPages/SaveForLaterButton";
import MarkAsCompleteButton from "../utilPages/MarkAsCompleteButton";
import { updateUser } from "../../services/userService";

const SavedPractices = () => {
  const { language, token, user, setUser, setToaster, premiumRoles } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPractice, setCurrentPractice] = useState({});
  const [practices, setPractices] = useState([]);
  const [selectionStates, setSelectionStates] = useState({
    isComplete: false,
    isSaved: false,
  });

  const levelOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "beginner", name: WorkoutTrans[language].beginner },
      { key: "experienced", name: WorkoutTrans[language].experienced },
      { key: "advanced", name: WorkoutTrans[language].advanced },
    ],
    [language]
  );

  const typeOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "recovery", name: WorkoutTrans[language].recovery },
      { key: "progression", name: WorkoutTrans[language].progression },
    ],
    [language]
  );

  const durationOptions = useMemo(
    () => [
      { key: null, name: WorkoutTrans[language].anyFem },
      { key: 15, name: WorkoutTrans[language].fifteen },
      { key: 30, name: WorkoutTrans[language].thirty },
      { key: 60, name: WorkoutTrans[language].sixty },
      { key: 90, name: WorkoutTrans[language].ninety },
    ],
    [language]
  );

  const [selectedLevel, setSelectedLevel] = useState(levelOptions[0]);
  const [selectedType, setSelectedType] = useState(typeOptions[0]);
  const [selectedDuration, setSelectedDuration] = useState(durationOptions[0]);

  const updateUserFavoritesAndCompletions = useCallback(async (practiceId, isSaved, isComplete) => {
    try {
      const currentSaved = user?.savedPractices || [];
      const currentCompleted = user?.completedPractices || [];
      const updatedUser = await updateUser(token, {
        savedPractices: isSaved
          ? Array.from(new Set([...currentSaved, practiceId]))
          : currentSaved.filter(id => id !== practiceId),
        completedPractices: isComplete
          ? Array.from(new Set([...currentCompleted, practiceId]))
          : currentCompleted.filter(id => id !== practiceId),
      });
      setUser(updatedUser);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  }, [token, user, setUser]);
  

  const handleRestrictedAction = (action) => {
    if (!user) {
      setToaster({message: ToasterTrans[language].login, type: "info", show: true});
    } else {
      action();
    }
  };

  const toggleSaved = () => {
    setSelectionStates((prev) => {
      const newSavedState = !prev.isSaved;
      updateUserFavoritesAndCompletions(currentPractice.uuid, newSavedState, prev.isComplete);
      return { ...prev, isSaved: newSavedState };
    });
  };

  const toggleComplete = () => {
    setSelectionStates((prev) => {
      const newCompleteState = !prev.isComplete;
      updateUserFavoritesAndCompletions(currentPractice.uuid, prev.isSaved, newCompleteState);
      return { ...prev, isComplete: newCompleteState };
    });
  };

  const openModal = (practice) => {
    setCurrentPractice(practice);
    setSelectionStates({
      isComplete: user?.completedPractices?.includes(practice.uuid) || false,
      isSaved: user?.savedPractices?.includes(practice.uuid) || false,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectionStates({ isComplete: false, isSaved: false });
    setCurrentPractice({});
  };

  // Refetch user on mount so saved list is up to date (e.g. after opening /saved in new tab)
  useEffect(() => {
    if (token) {
      getUser(token).then(setUser).catch(() => {});
    }
  }, [token]);

  useEffect(() => {
    if (!practices.length) {
      (async () => {
        const fetchedPractices = await getPractices();
        setPractices(fetchedPractices);
      })();
    }
  }, [practices.length]);
  
  // Filter the practices based on the user’s role. When no filters selected, show all saved; otherwise apply level/type/duration
  const filteredPractices = useMemo(() => {
    const isAdminOrPremium = premiumRoles.includes(user?.role);
    const savedIds = (user?.savedPractices || []).map((id) => String(id));
    const savedPracticesSet = new Set(savedIds);

    const noFilters = selectedLevel.key === "" && selectedType.key === "" && selectedDuration.key === null;

    const filtered = practices.filter((practice) => {
      const practiceId = practice.uuid != null ? String(practice.uuid) : (practice._id && String(practice._id));
      const matchesSaved = practiceId && savedPracticesSet.has(practiceId);
      if (!matchesSaved) return false;

      const matchesScope = isAdminOrPremium || practice.scope === "regular";
      if (!matchesScope) return false;

      if (noFilters) return true;

      const matchesLevel = selectedLevel.key === "" || (practice.level && practice.level.includes && practice.level.includes(selectedLevel.key));
      const matchesType = selectedType.key === "" || practice.type === selectedType.key || (Array.isArray(practice.type) && practice.type.includes(selectedType.key));
      const matchesDuration =
        selectedDuration.key === null ||
        (selectedDuration.key === 15 && practice.duration <= 15) ||
        (selectedDuration.key === 30 && practice.duration > 15 && practice.duration <= 45) ||
        (selectedDuration.key === 60 && practice.duration > 45 && practice.duration <= 75) ||
        (selectedDuration.key === 90 && practice.duration > 75 && practice.duration <= 105);

      return matchesLevel && matchesType && matchesDuration;
    });

    return filtered;
  }, [practices, selectedLevel, selectedType, selectedDuration, user, premiumRoles]);

  const getPracticeVideoUrl = (practice) =>
    practice?.videos?.[language]?.default ||
    practice?.videos?.ru?.default ||
    practice?.videos?.en?.default ||
    practice?.videoURL ||
    "";

  const getPracticeCoverPhotoUrl = (practice) =>
    practice?.coverPhotos?.[language] ||
    practice?.coverPhotos?.ru ||
    practice?.coverPhotos?.en ||
    "";

  return (
    <main className="flex min-h-screen flex-col mb-20">
      <div className="h-[55vh] w-full overflow-hidden">
        <img
          src={bgImg}
          alt="Description"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="w-10/12 mx-auto">
        <h1>{LibraryTrans[language].saved}</h1>
        <div className="flex flex-col sm:flex-row justify-between items-center mt-5 w-11/12 mx-auto">
          <div className="flex flex-wrap gap-4 sm:gap-14">
            <DropdownButton
              title={WorkoutTrans[language].level}
              options={levelOptions}
              selectedOption={selectedLevel}
              setSelectedOption={setSelectedLevel}
            />
            <DropdownButton
              title={WorkoutTrans[language].type}
              options={typeOptions}
              selectedOption={selectedType}
              setSelectedOption={setSelectedType}
            />
          </div>
          <div className="mt-4 sm:mt-0">
            <DropdownButton
              title={WorkoutTrans[language].duration}
              options={durationOptions}
              selectedOption={selectedDuration}
              setSelectedOption={setSelectedDuration}
            />
          </div>
        </div>
        <div className="container mx-auto w-11/12">
          {!user ? (
            <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
              {LibraryTrans[language].loginToSeeSaved}{" "}
              <Link to="/profile" className="underline font-medium">{LibraryTrans[language].saved}</Link>
            </p>
          ) : !user.savedPractices?.length ? (
            <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
              {LibraryTrans[language].noSavedYet}{" "}
              <Link to="/library" className="underline font-medium">{LibraryTrans[language].browseLibrary}</Link> {LibraryTrans[language].toSaveSome}
            </p>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mt-8">
            {filteredPractices.length === 0 && practices.length > 0 ? (
              <p className="col-span-full text-center text-gray-600 dark:text-gray-400 mt-4">
                {LibraryTrans[language].noMatchFilters}
              </p>
            ) : (
            filteredPractices.map((practice, index) => (
              <div key={practice.uuid || index}>
                <img
                  src={getPracticeCoverPhotoUrl(practice)}
                  alt={`Video thumbnail ${index + 1}`}
                  onClick={() => openModal(practice)}
                  className="cursor-pointer m-auto rounded-lg"
                />
              </div>
            ))
            )}
          </div>
          )}
        </div>
      </div>
      {isModalOpen && (
        <PopupModal onClose={closeModal}>
          <div className="flex flex-col h-full">
            <VideoDisplay videoUrl={getPracticeVideoUrl(currentPractice)} />
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs md:text-sm">
              <SaveForLaterButton
                isSaved={selectionStates.isSaved}
                toggleSave={() => handleRestrictedAction(toggleSaved)}
                variant="elevated"
              />
              <MarkAsCompleteButton
                isComplete={selectionStates.isComplete}
                toggleComplete={() => handleRestrictedAction(toggleComplete)}
                variant="elevated"
              />
            </div>
          </div>
        </PopupModal>
      )}
    </main>
  );
};

export default SavedPractices;
